/* eslint-disable react/jsx-no-bind */
import { CheckCircle, Settings } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Select,
  InputLabel,
  FormControl,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  CircularProgress,
  Grid,
} from '@mui/material';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import logger from '../../utils/logger';

import adminService from './adminService';

const roles = ['Super Administrator', 'Dispatcher Admin', 'Billing Admin'];
const statuses = ['Active', 'Inactive'];

// const initialData = [
//   {
//     id: 1,
//     name: "V3C Driver",
//     email: "v3cdriver3@gmail.com",
//     role: "Super Administrator",
//     status: "Active",
//   },
//   {
//     id: 2,
//     name: "Dispatcher Admin",
//     email: "dispatcher@demo.com",
//     role: "Dispatcher Admin",
//     status: "Active",
//   },
//   {
//     id: 3,
//     name: "Billing Admin",
//     email: "billing@demo.com",
//     role: "Billing Admin",
//     status: "Inactive",
//   },
// ];

// const API_URL = "http://localhost:3031/admins";

const Admin = () => {
  const [data, setData] = useState([]);
  const [allFetchedData, setAllFetchedData] = useState([]); // Store unfiltered data
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [errors, setErrors] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const isFetchingRef = useRef(false);

  // Store applied filters (used when pagination changes)
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    roleFilter: '',
    statusFilter: '',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: '',
    roleId: 1,
  });
  const navigate = useNavigate();
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const fetchAdmins = useCallback(
    async (
      pageNum = 1,
      pageSize = 5,
      searchParam = '',
      roleFilterParam = '',
    ) => {
      // Prevent duplicate concurrent calls
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setLoading(true);
      try {
        const response = await adminService.fetchAdmins(
          pageNum,
          pageSize,
          searchParam,
          roleFilterParam,
        );
        const admins = response.data?.data.admin_users || [];
        setAllFetchedData(admins);
        setTotalCount(response.data?.data?.total ?? 0);
      } catch (error) {
        logger.error('Error fetching admins:', error);
        toast.error('Error fetching admins');
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [],
  );

  // Apply local status filtering
  useEffect(() => {
    let filteredData = [...allFetchedData];

    if (appliedFilters.statusFilter) {
      filteredData = filteredData.filter((item) => {
        if (appliedFilters.statusFilter === 'Active') {
          return item.isActive === true;
        } else if (appliedFilters.statusFilter === 'Inactive') {
          return item.isActive === false;
        }
        return true;
      });
    }

    setData(filteredData);
  }, [allFetchedData, appliedFilters.statusFilter]);

  useEffect(() => {
    // Initial fetch on mount and when page/rowsPerPage changes
    // Filters are applied only when "Search" button is clicked
    // Status filter is applied locally, not sent to API
    fetchAdmins(
      page + 1,
      rowsPerPage,
      appliedFilters.search,
      appliedFilters.roleFilter,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const validateInputs = () => {
    let valid = true;
    const newErrors = { name: '', email: '', password: '' };

    if (!newAdmin.name.trim()) {
      newErrors.name = 'Name is required.';
      valid = false;
    }
    if (newAdmin.name.trim().length < 3) {
      newErrors.name = 'Minimum length must be 3';
      valid = false;
    }
    // Only validate email in add mode, not in edit mode
    if (!isEditMode) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newAdmin.email)) {
        newErrors.email = 'Enter a valid email.';
        valid = false;
      }
    }
    // Password validation: required for new admin, optional for edit (but must be at least 6 chars if provided)
    if (isEditMode) {
      // In edit mode, password is optional, but if provided, it must be at least 6 characters
      if (newAdmin.password && newAdmin.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters.';
        valid = false;
      }
    } else {
      // In add mode, password is required
      if (!newAdmin.password || newAdmin.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters.';
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleAddAdmin = async () => {
    if (validateInputs()) {
      try {
        await adminService.createAdmin(newAdmin);
        toast.success('Admin added successfully');
        fetchAdmins(
          page + 1,
          rowsPerPage,
          appliedFilters.search,
          appliedFilters.roleFilter,
        );
        handleModalClose();
      } catch (error) {
        logger.error('Error adding admin:', error);
        toast.error('Error adding admin');
      }
    }
  };

  const handleEditAdmin = async () => {
    if (validateInputs()) {
      try {
        // Build edit payload - only include name and password (if provided)
        const editPayload = {
          name: newAdmin.name,
        };

        // Only include password if it's provided (not empty)
        if (newAdmin.password && newAdmin.password.trim().length > 0) {
          editPayload.password = newAdmin.password;
        }

        await adminService.updateAdmin(selectedAdmin.userId, editPayload);
        toast.success('Admin updated successfully');
        fetchAdmins(
          page + 1,
          rowsPerPage,
          appliedFilters.search,
          appliedFilters.roleFilter,
        );
        handleModalClose();
      } catch (error) {
        logger.error('Error editing admin:', error);
        toast.error('Error updating admin');
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await adminService.deleteAdmin(selectedAdmin.userId);
        toast.success('Admin deleted successfully');
        fetchAdmins(
          page + 1,
          rowsPerPage,
          appliedFilters.search,
          appliedFilters.roleFilter,
        );
        handleMenuClose();
      } catch (error) {
        logger.error('Error deleting admin:', error);
        toast.error('Error deleting admin');
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedFilters({
      search,
      roleFilter,
      statusFilter,
    });
    // Status filter is applied locally, not sent to API
    fetchAdmins(1, rowsPerPage, search, roleFilter);
  };

  const handleReset = () => {
    setSearch('');
    setRoleFilter('');
    setStatusFilter('');
    setPage(0);
    setAppliedFilters({
      search: '',
      roleFilter: '',
      statusFilter: '',
    });
    // Fetch with reset values (status filter is applied locally)
    fetchAdmins(1, rowsPerPage, '', '');
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedAdmin(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleModalOpen = (editMode = false) => {
    setIsEditMode(editMode);
    if (editMode && selectedAdmin) {
      setNewAdmin({
        name: selectedAdmin.name,
        email: selectedAdmin.email,
        password: '',
        roleId: selectedAdmin.roleId,
      });
    } else {
      setNewAdmin({ name: '', email: '', password: '', roleId: 1 });
    }
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setNewAdmin({ name: '', email: '', password: '', roleId: 1 });
    setErrors({ name: '', email: '', password: '' });
    setSelectedAdmin(null);
  };
  const handleViewDetails = (admin, Id) => {
    navigate(`/admin/${Id}`, { state: { admin } });
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: { xs: 0.5, sm: 1 },
          margin: { xs: 1, sm: 2 },
          marginLeft: { xs: 1, sm: 2, md: 4 },
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Admins</h3>
          </Box>
          <Divider />
          <Box sx={{ padding: 4 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Search by Name or Email"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Filter by Role"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={roleFilter}
                  onChange={handleRoleFilterChange}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === '' || !selected) {
                        return 'All Roles';
                      }
                      return selected;
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                      transform: 'translate(14px, 10px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                  }}
                >
                  <DropdownItem value="">All Roles</DropdownItem>
                  <DropdownItem value="Admin">Admin</DropdownItem>
                  <DropdownItem value="User">User</DropdownItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Filter by Status"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === '' || !selected) {
                        return 'All Statuses';
                      }
                      return selected;
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                      transform: 'translate(14px, 10px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                  }}
                >
                  <DropdownItem value="">All Statuses</DropdownItem>
                  <DropdownItem value="Active">Active</DropdownItem>
                  <DropdownItem value="Inactive">Inactive</DropdownItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{
                      bgcolor: colors.swiftcarry[100],
                      '&:hover': {
                        bgcolor: colors.swiftcarry[100],
                      },
                      height: '40px',
                      flex: 1,
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{
                      borderColor: '#ccc',
                      color: '#666',
                      height: '40px',
                      flex: 1,
                      '&:hover': {
                        borderColor: colors.swiftcarry[100],
                        color: colors.swiftcarry[100],
                        backgroundColor: `${colors.swiftcarry[100]}10`,
                      },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleModalOpen(false)}
                    sx={{
                      bgcolor: colors.swiftcarry[100],
                      '&:hover': {
                        bgcolor: colors.swiftcarry[100],
                      },
                      height: '40px',
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <TableContainer
              component={Paper}
              sx={{
                overflowX: 'auto',
                position: 'relative',
                minHeight: 200,
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <Table
                sx={{
                  minWidth: { xs: 600, sm: 650 },
                  '& .MuiTableCell-root': {
                    padding: { xs: '8px 4px', sm: '16px' },
                    fontSize: { xs: '12px', sm: '14px' },
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Roles</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length > 0 ? (
                    data.map((row) => (
                      <TableRow key={row.userId}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          {row.roleId === 1 ? 'Admin' : 'User'}
                        </TableCell>
                        <TableCell>
                          {row.isActive ? (
                            <CheckCircle color="success" />
                          ) : (
                            <CancelIcon color="error" />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, row)}
                          >
                            <Settings />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No admins found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                width: '100%',
                overflow: 'auto',
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: { xs: 1, sm: 2 },
                  paddingRight: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                  minHeight: { xs: '52px', sm: '64px' },
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-select': {
                  marginRight: { xs: 1, sm: 2 },
                  marginLeft: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: { xs: 0.5, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                },
                '& .MuiIconButton-root': {
                  padding: { xs: '4px', sm: '8px' },
                },
                '& .MuiTablePagination-input': {
                  marginRight: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-menuItem': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
              }}
            />

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <DropdownItem
                onClick={() => {
                  handleViewDetails(selectedAdmin, selectedAdmin.userId);
                  handleMenuClose();
                }}
              >
                View
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  handleModalOpen(true);
                  handleMenuClose();
                }}
              >
                Edit
              </DropdownItem>
              <DropdownItem onClick={handleDelete}>Delete</DropdownItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            maxWidth: '400px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {isEditMode ? 'Edit Admin' : 'Add New Admin'}
          </Typography>
          <TextField
            label="Name"
            variant="outlined"
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: colors.swiftcarry[100],
                  color: colors.swiftcarry[100],
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
              },
              mb: 2,
            }}
            value={newAdmin.name}
            onChange={(e) => {
              setNewAdmin({ ...newAdmin, name: e.target.value });
              setErrors({ ...errors, name: '' });
            }}
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            size="small"
            disabled={isEditMode}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: colors.swiftcarry[100],
                  color: colors.swiftcarry[100],
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
              },
              mb: 2,
            }}
            value={newAdmin.email}
            onChange={(e) => {
              setNewAdmin({ ...newAdmin, email: e.target.value });
              setErrors({ ...errors, email: '' });
            }}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            size="small"
            placeholder={isEditMode ? 'Enter new password' : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: colors.swiftcarry[100],
                  color: colors.swiftcarry[100],
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
              },
              mb: 2,
            }}
            value={newAdmin.password}
            onChange={(e) => {
              setNewAdmin({ ...newAdmin, password: e.target.value });
              setErrors({ ...errors, password: '' });
            }}
            error={!!errors.password}
            helperText={errors.password}
          />
          <FormControl
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: colors.swiftcarry[100],
                  color: colors.swiftcarry[100],
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
              },
              mb: 2,
            }}
          >
            <InputLabel>Role</InputLabel>
            <Select
              value={newAdmin.roleId}
              onChange={(e) =>
                setNewAdmin({ ...newAdmin, roleId: e.target.value })
              }
              disabled={isEditMode}
              label="Role"
            >
              <MenuItem value={1}>Admin</MenuItem>
              <MenuItem value={2}>User</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={isEditMode ? handleEditAdmin : handleAddAdmin}
              disabled={
                !newAdmin.name ||
                (!isEditMode && (!newAdmin.email || !newAdmin.password))
              }
              sx={{
                bgcolor: colors.swiftcarry[100],
                '&:hover': {
                  bgcolor: colors.swiftcarry[100],
                },
              }}
            >
              {isEditMode ? 'Save Changes' : 'Add'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default Admin;
